/**
 * File doctor — UI layer.
 *
 * Two independent opinions, because they fail differently:
 *   1. STRUCTURE (probes.js) — walks headers/indexes. Sees a missing moov, a
 *      failed chunk CRC, a truncated package. Blind to rot inside compressed
 *      data: a JPEG with 1 000 flipped bits still has a perfect marker chain.
 *   2. DECODE — hands the bytes to the browser's own image/audio/video decoder.
 *      Sees exactly what the structure walk cannot, and is the same verdict the
 *      user's own viewer will reach.
 *
 * Reporting both is the point. "Structure intact, but your browser cannot decode
 * it" and "structure broken, yet it still plays" are different problems with
 * different fixes, and every competitor collapses them into one word.
 */

import { diagnose, VERDICT } from './diagnose.js';

const T = (k, p) => {
  let s = (window.__FD_I18N && window.__FD_I18N[k]) || k;
  if (p) for (const [a, b] of Object.entries(p)) s = s.replace(new RegExp(':' + a, 'g'), b);
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const ROUTES = (window.__FD_ROUTES) || {};

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb'), T('unit_tb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

/** Verdict → the tone the card is painted in. */
const TONE = {
  [VERDICT.HEALTHY]: 'ok',
  [VERDICT.RENAMED]: 'warn',
  [VERDICT.ENCRYPTED]: 'warn',
  [VERDICT.JUNK_PREFIX]: 'warn',
  [VERDICT.NO_INDEX]: 'bad',
  [VERDICT.TRUNCATED]: 'bad',
  [VERDICT.BODY_DAMAGED]: 'bad',
  [VERDICT.HEADER_DESTROYED]: 'bad',
  [VERDICT.EMPTY]: 'dead',
  [VERDICT.UNKNOWN]: 'unknown',
};

// ───────────────────────────── decode verification ───────────────────────────
/**
 * Ask the platform to actually render/decode the file.
 *
 * Returns {tried, ok, note}. `tried:false` means we have no decoder for this
 * family — reported as "not checked", never as "fine". Silence dressed up as a
 * pass is how a tool earns the wrong kind of trust.
 */
async function verifyByDecoding(file, res) {
  const fam = res.family;
  try {
    if (fam === 'image') {
      const bmp = await createImageBitmap(file).catch(() => null);
      if (!bmp) return { tried: true, ok: false, note: 'decode_image_failed' };
      const note = res.facts.find((f) => f.k === 'dim');
      const claim = note && note.v;
      const real = `${bmp.width}×${bmp.height}`;
      bmp.close && bmp.close();
      // A decoder that silently returns a smaller image decoded only the part
      // that survived — that discrepancy IS the damage report.
      if (claim && claim !== real) return { tried: true, ok: false, note: 'decode_size_mismatch', claim, real };
      return { tried: true, ok: true, real };
    }
    if (fam === 'audio' || fam === 'video') {
      return await new Promise((resolve) => {
        const node = document.createElement(fam === 'audio' ? 'audio' : 'video');
        const url = URL.createObjectURL(file);
        let done = false;
        const finish = (v) => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          URL.revokeObjectURL(url);
          node.removeAttribute('src');
          resolve(v);
        };
        const timer = setTimeout(() => finish({ tried: true, ok: false, note: 'decode_timeout' }), 8000);
        node.preload = 'metadata';
        node.muted = true;
        node.onloadedmetadata = () => finish({
          tried: true,
          ok: Number.isFinite(node.duration) && node.duration > 0,
          note: Number.isFinite(node.duration) && node.duration > 0 ? null : 'decode_no_duration',
          real: Number.isFinite(node.duration) ? `${node.duration.toFixed(1)}s` : null,
        });
        node.onerror = () => finish({ tried: true, ok: false, note: 'decode_media_failed' });
        node.src = url;
      });
    }
  } catch (_) {
    return { tried: true, ok: false, note: 'decode_threw' };
  }
  return { tried: false, ok: null, note: null };
}

// ────────────────────────────────── rendering ────────────────────────────────
function renderVerdict(res, decode) {
  const tone = TONE[res.verdict] || 'unknown';
  const pct = res.survived == null ? null : Math.round(res.survived * 100);

  const chips = [];
  chips.push(`<span class="fd-chip">${esc(res.label || T('fmt_unknown'))}</span>`);
  chips.push(`<span class="fd-chip">${human(res.size)}</span>`);
  if (res.offset) chips.push(`<span class="fd-chip fd-chip-warn">${T('chip_starts_at', { at: human(res.offset) })}</span>`);
  if (res.confidence && res.confidence < 0.6) chips.push(`<span class="fd-chip fd-chip-warn">${T('chip_low_confidence')}</span>`);

  let bar = '';
  if (pct != null && res.verdict !== VERDICT.HEALTHY) {
    bar = `<div class="fd-bar" role="img" aria-label="${T('survived_label')}: ${pct}%">
        <div class="fd-bar-fill fd-bar-${tone}" style="width:${Math.max(2, pct)}%"></div>
        <span class="fd-bar-txt">${T('survived_pct', { pct })}</span>
      </div>`;
  }

  return `<div class="fd-verdict fd-tone-${tone}">
      <div class="fd-verdict-head">
        <span class="fd-verdict-title">${T('verdict_' + res.verdict)}</span>
        <div class="fd-chips">${chips.join('')}</div>
      </div>
      <p class="fd-verdict-body">${T('verdict_body_' + res.verdict)}</p>
      ${bar}
    </div>`;
}

function renderIssues(res) {
  if (!res.issues.length) return '';
  const seen = new Set();
  const items = res.issues.filter((i) => {
    if (seen.has(i.code)) return false;
    seen.add(i.code);
    return true;
  }).map((i) => {
    const n = res.issues.filter((x) => x.code === i.code).length;
    const params = {
      at: i.at != null ? human(i.at) : '',
      want: i.want != null ? String(i.want) : '',
      got: i.got != null ? String(i.got) : '',
      n: String(n),
    };
    const extra = n > 1 ? ` <span class="fd-issue-n">×${n}</span>` : '';
    return `<li><span class="fd-issue-code">${T('issue_' + i.code, params)}</span>${extra}</li>`;
  });
  return `<section class="fd-block"><h3>${T('issues_title')}</h3><ul class="fd-issues">${items.join('')}</ul></section>`;
}

function renderDecode(res, d) {
  if (!d) return '';
  let body, cls;
  if (!d.tried) { body = T('decode_skipped'); cls = 'muted'; }
  else if (d.ok) { body = T('decode_ok', { real: d.real || '' }); cls = 'ok'; }
  else {
    // `note` already carries its `decode_` prefix — re-adding one produced the
    // literal key `decode_decode_media_failed` on screen.
    body = T(d.note || 'decode_failed', { claim: d.claim || '', real: d.real || '' });
    cls = 'bad';
  }

  // The disagreement is more informative than either verdict alone.
  let conflict = '';
  const structOk = res.verdict === VERDICT.HEALTHY;
  if (d.tried && structOk && d.ok === false) conflict = `<p class="fd-conflict">${T('conflict_struct_ok_decode_bad')}</p>`;
  if (d.tried && !structOk && d.ok === true) conflict = `<p class="fd-conflict">${T('conflict_struct_bad_decode_ok')}</p>`;

  return `<section class="fd-block"><h3>${T('decode_title')}</h3>
      <p class="fd-decode fd-decode-${cls}">${body}</p>${conflict}</section>`;
}

function renderFacts(res) {
  if (!res.facts.length) return '';
  const rows = res.facts.map((f) =>
    `<tr><th scope="row">${T('fact_' + f.k)}</th><td>${esc(f.v)}</td></tr>`).join('');
  return `<section class="fd-block"><h3>${T('facts_title')}</h3>
      <table class="fd-facts">${rows}</table></section>`;
}

function renderNames(res) {
  if (!res.names || !res.names.length) return '';
  const bad = new Set(res.badEntries || []);
  const shown = res.names.slice(0, 60).map((n) =>
    `<li class="${bad.has(n) ? 'fd-name-bad' : ''}">${esc(n)}${bad.has(n) ? ` <span class="fd-issue-n">${T('entry_crc_bad')}</span>` : ''}</li>`).join('');
  const more = res.names.length > 60 ? `<p class="fd-more">${T('names_more', { n: res.names.length - 60 })}</p>` : '';
  return `<section class="fd-block"><h3>${T('names_title', { n: res.names.length })}</h3>
      <ul class="fd-names">${shown}</ul>${more}</section>`;
}

/**
 * The routing block — the reason this tool exists.
 *
 * A diagnosis with nowhere to go is why `disk-image-inspector` has served one
 * session in its life. Every verdict that something on this site can act on ends
 * in a button that opens it.
 */
function renderRoutes(res) {
  const out = [];
  const add = (slug, key) => {
    if (!slug || !ROUTES[slug]) return;
    out.push(`<a class="fd-route lg-btn" href="${ROUTES[slug]}" data-fd-route="${esc(slug)}">
        <span class="fd-route-t">${T('route_' + key)}</span>
        <span class="fd-route-s">${T('route_tool_' + slug)}</span></a>`);
  };

  if (res.verdict === VERDICT.RENAMED && res.suggestExt) {
    out.push(`<button type="button" class="fd-route lg-btn" id="fd-rename">
        <span class="fd-route-t">${T('route_rename', { ext: res.suggestExt })}</span>
        <span class="fd-route-s">${T('route_rename_sub')}</span></button>`);
  }
  if (res.offset > 0) {
    out.push(`<button type="button" class="fd-route lg-btn" id="fd-strip">
        <span class="fd-route-t">${T('route_strip')}</span>
        <span class="fd-route-s">${T('route_strip_sub', { at: human(res.offset) })}</span></button>`);
  }
  // A missing index and a destroyed header are both fixed FROM A REFERENCE FILE,
  // so they get the "rebuild from a healthy twin" wording rather than plain "repair".
  const byReference = res.verdict === VERDICT.NO_INDEX || res.verdict === VERDICT.HEADER_DESTROYED;
  if (res.repair && res.verdict !== VERDICT.HEALTHY) add(res.repair, byReference ? 'donor' : 'repair');
  if (res.verdict === VERDICT.EMPTY) { add('usb-drive-repair', 'media'); add('usb-drive-tester', 'media_test'); }

  if (!out.length) return '';
  return `<section class="fd-block fd-routes-block"><h3>${T('routes_title')}</h3>
      <div class="fd-routes">${out.join('')}</div></section>`;
}

// ─────────────────────────────────── driver ──────────────────────────────────
let current = null;

async function analyse(file) {
  const out = el('fd-out');
  out.innerHTML = `<div class="fd-working">${T('working')}</div>`;
  out.hidden = false;
  window.__stage && window.__stage('analyzing');

  let res;
  try {
    res = await diagnose(file, file.name, {
      onProgress: ({ phase }) => {
        const w = out.querySelector('.fd-working');
        if (w) w.textContent = T('phase_' + phase);
      },
    });
  } catch (err) {
    window.__handleError && window.__handleError(err, 'file-doctor');
    /* A diagnosis that itself crashed is the worst outcome this tool has, and
     * `__handleError` only reaches the console. Without an event it looks
     * identical to a person who opened the page and left. */
    window.__aeToolError && window.__aeToolError({
      errorType: 'analyze_failed',
      message: String((err && err.message) || err).slice(0, 140),
      ext: (file.name || '').split('.').pop().toLowerCase(),
      size: file.size,
    });
    out.innerHTML = `<div class="fd-verdict fd-tone-unknown"><p>${T('err_analyze')}</p></div>`;
    return;
  }

  const decode = await verifyByDecoding(file, res);
  current = { res, decode, file };

  out.innerHTML = [
    renderVerdict(res, decode),
    renderIssues(res),
    renderDecode(res, decode),
    renderRoutes(res),
    renderFacts(res),
    renderNames(res),
  ].join('');

  wireActions();
  window.__stage && window.__stage('result');
  window.__aeToolComplete && window.__aeToolComplete({
    verdict: res.verdict, format: res.format || 'none', family: res.family || 'none',
    issues: res.issues.length, survived: res.survived == null ? -1 : Math.round(res.survived * 100),
    decode_tried: decode.tried ? 1 : 0, decode_ok: decode.ok === true ? 1 : 0,
    size: res.size, ext: res.ext || 'none',
  });
  window.__announce && window.__announce(T('verdict_' + res.verdict));
}

function wireActions() {
  const rn = el('fd-rename');
  if (rn) rn.addEventListener('click', () => {
    const { res, file } = current;
    const base = (file.name || 'file').replace(/\.[^.]+$/, '');
    const want = (res.exts && res.exts[0]) || res.suggestExt;
    window.__downloadFile(file, `${base}.${want}`);
    window.__aeFunnel && window.__aeFunnel('rename_applied', { to: want });
  });

  const st = el('fd-strip');
  if (st) st.addEventListener('click', async () => {
    const { res, file } = current;
    // Same bytes, minus the glued-on prefix. No re-encode: whatever was wrong
    // with the wrapper, the payload is untouched and must stay that way.
    const blob = file.slice(res.offset);
    const base = (file.name || 'file').replace(/\.[^.]+$/, '');
    const want = (res.exts && res.exts[0]) || res.ext || 'bin';
    window.__downloadFile(blob, `${base}-fixed.${want}`);
    window.__aeFunnel && window.__aeFunnel('prefix_stripped', { at: res.offset });
  });

  document.querySelectorAll('[data-fd-route]').forEach((a) => {
    a.addEventListener('click', () => {
      window.__aeFunnel && window.__aeFunnel('route_click', { to: a.dataset.fdRoute });
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('fdDrop');
  if (!zone) return;
  /* The shared uploader owns validation, byte-priming (FILE_LOST guard) and
   * telemetry; its `tool-uploader:file` event is the only supported entry
   * point. The hidden <input> lives on <body>, so listening for `change` on a
   * child would silently never fire. */
  zone.addEventListener('tool-uploader:file', (e) => {
    const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
    if (f) analyse(f);
  });
});

export { analyse };
